import { computed } from 'vue';

const ERROR_STATUSES = ['error', 'failed', 'failure', 'cancelled', 'aborted'];
const FINISHED_STATUSES = [
  'success',
  'succeeded',
  'finished',
  'complete',
  'completed',
  'done',
];
const PENDING_STATUSES = ['pending', 'queued'];
const RUNNING_STATUSES = ['running', 'in_progress', 'started'];

function normalizeRawStatus(rawStatus, content) {
  if (ERROR_STATUSES.includes(rawStatus)) return 'error';
  if (FINISHED_STATUSES.includes(rawStatus)) return 'finished';
  if (PENDING_STATUSES.includes(rawStatus)) return 'pending';
  if (RUNNING_STATUSES.includes(rawStatus)) return 'running';

  const hasContent =
    (typeof content === 'string' && content.length > 0) ||
    (Array.isArray(content) && content.length > 0) ||
    (!!content && typeof content === 'object');
  return hasContent ? 'finished' : null;
}

function hasToolOutput(tc) {
  if (!tc) return false;
  const payload = tc.output ?? tc.result;
  if (payload == null) return false;
  if (typeof payload === 'string') return payload.length > 0;
  if (Array.isArray(payload)) return payload.length > 0;
  if (typeof payload === 'object') {
    if (Array.isArray(payload.content)) return payload.content.length > 0;
    if (typeof payload.content === 'string') return payload.content.length > 0;
    return Object.keys(payload).length > 0;
  }
  return true;
}

/**
 * Provides normalized tool call status tracking from stream messages and tool calls.
 */
export function useCopilotToolStatus({
  stream,
  hasPendingToolConsentInterrupt,
}) {
  const toolResultStatusById = computed(() =>
    (stream.messages.value || []).reduce((acc, msg) => {
      // eslint-disable-next-line no-underscore-dangle
      const type = msg?.type || msg?._getType?.();
      if (type !== 'tool') return acc;

      const tcId = msg?.tool_call_id || msg?.toolCallId;
      if (!tcId) return acc;

      const normalized = normalizeRawStatus(msg?.status, msg?.content);
      if (normalized) acc[String(tcId)] = normalized;
      return acc;
    }, {})
  );

  function normalizeToolCallStatus(tc) {
    const { status } = tc || {};
    const tcId = String(tc?.callId || tc?.id || '');
    const resultStatus = tcId ? toolResultStatusById.value[tcId] : null;

    if (resultStatus) return resultStatus;
    if (ERROR_STATUSES.includes(status)) return 'error';
    if (FINISHED_STATUSES.includes(status)) return 'finished';

    if (!stream.isLoading.value && !hasPendingToolConsentInterrupt.value) {
      return 'finished';
    }

    if (
      RUNNING_STATUSES.includes(status) ||
      PENDING_STATUSES.includes(status)
    ) {
      if (tc?.error) return 'error';
      if (hasToolOutput(tc)) return 'finished';
    }

    return status || 'running';
  }

  const toolCalls = computed(() =>
    (stream.toolCalls.value || []).map(tc => ({
      ...tc,
      status: normalizeToolCallStatus(tc),
    }))
  );

  return { toolCalls, toolResultStatusById };
}
